import {
  Alert,
  AlertDescription,
  AlertIcon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  usePrevious,
} from "@chakra-ui/react";
import { BatchItem } from "@rpldy/uploady";
import {
  useDistributionWithholdingByUploadUpsert,
  useDistributionWithholdingTemplateRetrieveSuspense,
} from "gen/endpoints/distributions/distributions";
import {
  documentStatusUploadCompleteUpdate,
  useDocumentRetrieve,
} from "gen/endpoints/documents/documents";
import { DistributionWithholdingUploadRequestDataRequest } from "gen/model/distributionWithholdingUploadRequestDataRequest";
import { DocumentCreateResponseData } from "gen/model/documentCreateResponseData";
import { DocumentStateMachineFieldsEnum } from "gen/model/documentStateMachineFieldsEnum";
import { DocumentTypeEnum } from "gen/model/documentTypeEnum";
import { get, noop } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FileUpload } from "shared/components/upload/FileUpload";
import { FileUploadHandle } from "shared/components/upload/FileUploadController";
import { uploadAcceptCsvAndExcel } from "shared/components/upload/FileUploadUtils";
import { downloadFileBlob } from "shared/utils/utils";

interface DistributionWithholdingUploadModalProps {
  fundFamilyId: string;
  fundFamilyEventId: string;

  onCloseComplete(): void;

  onSuccess(): void;
}

interface DistributionWithholdingUploadModalContentProp
  extends DistributionWithholdingUploadModalProps {
  onClose(): void;
}

const DistributionWithholdingUploadModalContent: React.FC<
  DistributionWithholdingUploadModalContentProp
> = ({
  fundFamilyId,
  fundFamilyEventId,
  onCloseComplete,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation(["common", "distribution"]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { data: excelTemplateBlob } =
    useDistributionWithholdingTemplateRetrieveSuspense(
      fundFamilyId,
      fundFamilyEventId,
    );
  const { mutateAsync: upsertDistributionWithholding } =
    useDistributionWithholdingByUploadUpsert();

  const onSubmit = async (
    data: DistributionWithholdingUploadRequestDataRequest,
  ) => {
    await upsertDistributionWithholding({
      fundFamilyId,
      fundFamilyEventId,
      data,
    });
  };

  const form = useForm<DistributionWithholdingUploadRequestDataRequest>();
  const documentId = form.watch("distribution_withholding_upload_document_id");

  const { data } = useDocumentRetrieve(documentId!, {
    query: {
      enabled: !!documentId,
      refetchInterval: 1000,
    },
  });

  const prevState = usePrevious(data?.state);

  const handleRef = useRef<FileUploadHandle>(null);
  const onItemsChanged = useCallback(
    (items: BatchItem[]) => {
      if (items.length === 0) {
        form.setValue("distribution_withholding_upload_document_id", undefined);
      }
    },
    [form],
  );

  const onItemFinished = async (item: BatchItem) => {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    const document: DocumentCreateResponseData = get(item, "document")!;
    await documentStatusUploadCompleteUpdate(document.id);
    setErrorMessage(undefined);
    form.setValue("distribution_withholding_upload_document_id", document.id);
  };

  const onClickDownload = () => {
    downloadFileBlob(
      excelTemplateBlob,
      t("distribution:withholding.addModal.filename"),
    );
  };

  useEffect(() => {
    if (data && prevState !== data.state) {
      if (data.state === DocumentStateMachineFieldsEnum.successful_ingestion) {
        onSuccess();
        onCloseComplete();
      } else if (
        data.state === DocumentStateMachineFieldsEnum.failed_ingestion
      ) {
        handleRef.current?.clear();
        setErrorMessage(data.error);
        form.setValue("distribution_withholding_upload_document_id", undefined);
      }
    }
  }, [data, form, onCloseComplete, onSuccess, prevState]);

  return (
    <ModalContent>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={noop}
        isDisabled={!documentId}
      >
        <ModalHeader>
          {t("distribution:withholding.addModal.title")}
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <Text mb="16px">
            <Trans
              ns="distribution"
              i18nKey="withholding.addModal.downloadText"
            >
              Download this
              <Link onClick={onClickDownload}>excel template</Link>
              to start your withholding.
            </Trans>
          </Text>
          <Text mb="16px">
            {t("distribution:withholding.addModal.uploadText")}
          </Text>
          <FileUpload
            documentType={DocumentTypeEnum.distribution_withholding}
            accept={uploadAcceptCsvAndExcel}
            onItemFinished={onItemFinished}
            handleRef={handleRef}
            onItemsChanged={onItemsChanged}
            canUpload={!documentId}
          />
          {errorMessage && (
            <Alert
              status="error"
              mt="16px"
              data-ls-enabled={true}
              data-public={true}
            >
              <AlertIcon />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <ButtonOrder
            dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
            confirm={
              <FormSubmitButton>{t("common:upload.upload")}</FormSubmitButton>
            }
          />
        </ModalFooter>
      </Form>
    </ModalContent>
  );
};

export const DistributionWithholdingUploadModal: React.FC<
  DistributionWithholdingUploadModalProps
> = ({ fundFamilyId, fundFamilyEventId, onCloseComplete, onSuccess }) => {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <QueryBoundary>
        <DistributionWithholdingUploadModalContent
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
          onCloseComplete={onCloseComplete}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      </QueryBoundary>
    </Modal>
  );
};
